import { useCallback, useState } from "react";
import { captureException } from "@sentry/nextjs";
import useAsyncCompleteToast from "components/Shared/useAsyncCompleteToast";
import { downloadFileUrl } from "./utils";

export type UseSignedUrlDownloadContext = {
  isLoading?: boolean;
};

export type UseSignedUrlDownloadReturnTuple = [
  handleDownload: (documentId: UUID, filename: string) => Promise<void>,
  context: UseSignedUrlDownloadContext,
];

export default function useSignedUrlDownload(
  getSignedUrl: (documentId: UUID, filename: string) => Promise<string>,
): UseSignedUrlDownloadReturnTuple {
  const [isLoading, setIsLoading] = useState(false);

  const { showErrorToast } = useAsyncCompleteToast();

  const performDownload = useCallback(
    async (documentId: UUID, filename: string) => {
      setIsLoading(true);
      try {
        const fileUrl = await getSignedUrl(documentId, filename);
        downloadFileUrl(fileUrl, filename);
      } catch (e) {
        captureException(e, { extra: { documentId, filename } });
        showErrorToast(e, {
          title: "An error occurred downloading the document",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [getSignedUrl, showErrorToast],
  );

  return [performDownload, { isLoading }];
}
